import { useCallback, useEffect, useState } from 'react';

export const FILTER_BAR_TRANSITION_DURATION = 300;

interface UseFilterBarPanelsOptions {
  isFilterPanelOpen: boolean;
  onToggleFilterPanel: () => void;
}

interface UseFilterBarPanelsResult {
  isSortPanelOpen: boolean;
  shouldRenderFilterOverlay: boolean;
  shouldRenderSortOverlay: boolean;
  toggleSortPanel: () => void;
  toggleFilterPanel: () => void;
  closeAllPanels: () => void;
}

const useFilterBarPanels = ({
  isFilterPanelOpen,
  onToggleFilterPanel,
}: UseFilterBarPanelsOptions): UseFilterBarPanelsResult => {
  const [shouldRenderFilterOverlay, setShouldRenderFilterOverlay] = useState(isFilterPanelOpen);
  const [isSortPanelOpen, setIsSortPanelOpen] = useState(false);
  const [shouldRenderSortOverlay, setShouldRenderSortOverlay] = useState(isSortPanelOpen);

  useEffect(() => {
    if (isFilterPanelOpen) {
      setShouldRenderFilterOverlay(true);
      return;
    }

    const timer = window.setTimeout(() => {
      setShouldRenderFilterOverlay(false);
    }, FILTER_BAR_TRANSITION_DURATION);

    return () => window.clearTimeout(timer);
  }, [isFilterPanelOpen]);

  useEffect(() => {
    if (isSortPanelOpen) {
      setShouldRenderSortOverlay(true);
      return;
    }

    const timer = window.setTimeout(() => {
      setShouldRenderSortOverlay(false);
    }, FILTER_BAR_TRANSITION_DURATION);

    return () => window.clearTimeout(timer);
  }, [isSortPanelOpen]);

  const closeAllPanels = useCallback(() => {
    if (isFilterPanelOpen) {
      onToggleFilterPanel();
    }
    if (isSortPanelOpen) {
      setIsSortPanelOpen(false);
    }
  }, [isFilterPanelOpen, isSortPanelOpen, onToggleFilterPanel]);

  const toggleSortPanel = useCallback(() => {
    setIsSortPanelOpen((previous) => !previous);
    if (isFilterPanelOpen) {
      onToggleFilterPanel();
    }
  }, [isFilterPanelOpen, onToggleFilterPanel]);

  const toggleFilterPanel = useCallback(() => {
    onToggleFilterPanel();
    if (isSortPanelOpen) {
      setIsSortPanelOpen(false);
    }
  }, [isSortPanelOpen, onToggleFilterPanel]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      if (!isFilterPanelOpen && !isSortPanelOpen) {
        return;
      }

      closeAllPanels();
      event.preventDefault();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeAllPanels, isFilterPanelOpen, isSortPanelOpen]);

  return {
    isSortPanelOpen,
    shouldRenderFilterOverlay,
    shouldRenderSortOverlay,
    toggleSortPanel,
    toggleFilterPanel,
    closeAllPanels,
  };
};

export default useFilterBarPanels;
